
import * as XLSX from 'xlsx';
import { CreateSundayMarketPriceInput } from '@/types/database/sunday-market-prices';
import { parseExcelDate } from './utils/dateUtils';
import { getColumnValue } from './utils/columnUtils';

export interface SundayMarketExcelRow {
  'Date': string | Date;
  'Timestamp': string | Date;
  'Product Name': string;
  'Product List': string;
  'Products List': string;
  'Price': string | number;
  'Unit Price': string | number;
  'Location': string;
  'Product Remark'?: string;
  'Product Remarks'?: string;
}

export const parseSundayMarketPricesFromExcel = (workbook: XLSX.WorkBook): {
  data: CreateSundayMarketPriceInput[];
  errors: string[];
} => {
  const errors: string[] = [];
  const data: CreateSundayMarketPriceInput[] = [];

  try {
    const firstSheetName = workbook.SheetNames[0];
    if (!firstSheetName) {
      errors.push('No sheets found in the Excel file');
      return { data, errors };
    }

    const worksheet = workbook.Sheets[firstSheetName];
    const jsonData = XLSX.utils.sheet_to_json(worksheet, { 
      header: 1,
      defval: null,
      raw: false 
    }) as any[][];

    if (jsonData.length < 2) {
      errors.push('Excel file must contain at least a header row and one data row');
      return { data, errors };
    }

    const headers = jsonData[0] as string[];
    const rows = jsonData.slice(1);

    console.log('Excel headers found:', headers);

    rows.forEach((row, index) => {
      const rowIndex = index + 2;
      
      try {
        const rowObj: Record<string, any> = {};
        headers.forEach((header, colIndex) => {
          if (header) {
            rowObj[header] = row[colIndex];
          }
        });

        if (Object.values(rowObj).every(value => !value)) {
          return;
        }

        const dateValue = getColumnValue(rowObj, ['Timestamp', 'Date']);
        const parsedDate = parseExcelDate(dateValue);
        
        if (!parsedDate) {
          errors.push(`Row ${rowIndex}: Invalid or missing date in Timestamp/Date column`);
          return;
        }

        const productName = getColumnValue(rowObj, ['Products List', 'Product List', 'Product Name']);
        if (!productName || productName.toString().trim() === '') {
          errors.push(`Row ${rowIndex}: Missing product name in Products List/Product List/Product Name column`);
          return;
        }

        const priceValue = getColumnValue(rowObj, ['Unit Price', 'Price']);
        let price = 0;
        
        if (priceValue !== null && priceValue !== undefined) {
          const priceStr = priceValue.toString().replace(/[^\d.-]/g, '');
          price = parseFloat(priceStr);
          
          if (isNaN(price)) {
            errors.push(`Row ${rowIndex}: Invalid price value "${priceValue}" in Unit Price/Price column`);
            return;
          }
        }

        const location = getColumnValue(rowObj, ['Location']);
        if (!location || location.toString().trim() === '') {
          errors.push(`Row ${rowIndex}: Missing location in Location column`);
          return;
        }

        const productRemarkValue = getColumnValue(rowObj, ['Product Remarks', 'Product Remark', 'Remark', 'Notes']);
        let productRemark: string | null = null;
        
        if (productRemarkValue !== null && productRemarkValue !== undefined) {
          const remarkStr = String(productRemarkValue).trim();
          
          if (remarkStr && 
              remarkStr !== 'undefined' && 
              remarkStr !== 'null' &&
              remarkStr !== 'N/A' &&
              remarkStr !== '') {
            productRemark = remarkStr;
          }
        }

        const sundayMarketPrice: CreateSundayMarketPriceInput = {
          date: parsedDate,
          product_name: productName.toString().trim(),
          price: price,
          location: location.toString().trim(),
          product_remark: productRemark,
        };

        console.log(`Row ${rowIndex} parsed:`, sundayMarketPrice);
        data.push(sundayMarketPrice);
        
      } catch (error) {
        console.error(`Error parsing row ${rowIndex}:`, error);
        errors.push(`Row ${rowIndex}: ${error instanceof Error ? error.message : 'Unknown parsing error'}`);
      }
    });

    console.log(`Successfully parsed ${data.length} records with ${errors.length} errors`);

  } catch (error) {
    console.error('Error parsing sunday market prices from Excel:', error);
    errors.push(`Failed to parse Excel file: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }

  return { data, errors };
};
